/* eslint-disable import/no-extraneous-dependencies, no-unused-vars */
import React, { useEffect, useState, useCallback, useMemo } from "react";

import { useDebouncedCallback } from "@qlik/qlik/src/hooks/useDebouncedCallback";
import { getFilterTaxonomyTree } from "@qlik/qlik/src/lib/util/filters";
import { EventConstants, TaxonomyMapping } from "@qlik/qlik/src/lib/constants/event-constants";
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import DropdownFilterGroup from "@qlik/qlik/src/components/Common/DropdownFilterGroup";
import NoResults from "@qlik/qlik/src/components/Common/NoResults";
import EventCard from "@qlik/qlik/src/components/CardDeckContainer/Event_Cards/ContentEventCard";
import FilterChips from "@qlik/qlik/src/components/Common/FilterChips";
import { FilterContext } from "@qlik/qlik/src/context/FilterContext";
import EventData from "@qlik/qlik/src/graphql/queries/events";
import { getMicrocopyValueFromKeys, renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import { useI18n } from "@qlik/utils/i18nContext";

/**
 * Displays a list of events with filtering and search functionality.
 *
 * @component
 * @param {Object} data - The data object containing configuration for the component.
 * @param {Object} data.cardItem - The configuration for the card item displayed alongside the events.
 * @returns {JSX.Element} The EventListing component.
 */
const EventListing = ({ data }) => {
    const [allParams, setAllParams] = useState([]);
    const [events, setEvents] = useState([]);
    const [selections, setSelections] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Valid filters based on current selections.
    const [validFilters, setValidFilters] = useState({});

    const { i18n } = useI18n();
    const eventData = EventData(i18n.curr.enLangName);

    const [resetFilterText, noResultsText, searchText] = getMicrocopyValueFromKeys(data?.microcopyGroup, [
        EventConstants.EVENT_CLEAR_FILTERS,
        EventConstants.EVENT_NO_RECORDS,
        EventConstants.EVENT_KEYWORD_PLACEHOLDER,
    ]);

    const shouldFilterByKeyword = allParams.keyword?.length;
    const shouldFilterByRegion = allParams.regions?.length;
    const shouldFilterByInterest = allParams.interests?.length;
    const shouldFilterByEventType = allParams.eventTypes?.length;

    const [searchTerm, setSearchTerm] = useState("");

    const eventTaxonomyTree = getFilterTaxonomyTree(eventData.eventTaxonomies, "Event");

    const handleFiltering = useCallback(
        (items, filterBy) => {
            // Filter events by Language -- for each element, if some of the elements languages match "All" or the
            // current language, show the event.
            let filteredEvents = items.filter((x) =>
                x?.languages?.some((y) => ["All", i18n.curr.enLangName].includes(y))
            );

            // Takes title, description and body fields into search
            if (filterBy.keyword) {
                filteredEvents = filteredEvents.filter((event) => {
                    const title = event?.title;
                    const description = event?.description;
                    let body = event?.body?.raw;
                    body = body ? documentToPlainTextString(JSON.parse(body)) : "";
                    return `${title} ${description} ${body}`.toLowerCase().includes(allParams?.keyword?.toLowerCase());
                });
            }

            if (filterBy.regions) {
                filteredEvents = filteredEvents.filter(
                    (event) => event.region && event.region.some((term) => allParams?.regions.includes(term.title))
                );
            }

            if (filterBy.interests) {
                filteredEvents = filteredEvents.filter(
                    (event) =>
                        event.productInterest &&
                        event.productInterest.some((term) => allParams?.interests.includes(term.title))
                );
            }

            if (filterBy.eventTypes) {
                filteredEvents = filteredEvents.filter(
                    (event) => event.type && event.type.some((term) => allParams?.eventTypes.includes(term.title))
                );
            }

            return filteredEvents;
        },
        [allParams]
    );

    const upcomingEvents = useMemo(() => {
        return eventData.events.nodes.filter((event) => new Date(event.endDate).getTime() >= Date.now());
    }, [eventData.events.nodes]);

    useEffect(() => {
        const filteredEvents = handleFiltering(upcomingEvents, {
            keyword: shouldFilterByKeyword,
            regions: shouldFilterByRegion,
            interests: shouldFilterByInterest,
            eventTypes: shouldFilterByEventType,
        });
        setEvents(filteredEvents);
        setIsLoading(false);
        const selectedFilters = Object.values(allParams).flat();
        setSelections(selectedFilters);
    }, [
        upcomingEvents,
        handleFiltering,
        allParams,
        shouldFilterByKeyword,
        shouldFilterByRegion,
        shouldFilterByInterest,
        shouldFilterByEventType,
    ]);

    // Disable checkboxes based on what's available with the current filters.
    useEffect(() => {
        const filters = ["region", "productInterest", "type"];

        const validFiltersObj = {};
        filters.forEach((currFilterType, i, arr) => {
            const filtered = handleFiltering(upcomingEvents, {
                keyword: shouldFilterByKeyword,
                regions: shouldFilterByRegion && currFilterType !== "region",
                interests: shouldFilterByInterest && currFilterType !== "productInterest",
                eventTypes: shouldFilterByEventType && currFilterType !== "type",
            });
            filtered.forEach((item) => {
                item?.[currFilterType]?.forEach((term) => {
                    validFiltersObj[term.title] = true;
                });
            });
        });
        setValidFilters(validFiltersObj);
    }, [
        upcomingEvents,
        handleFiltering,
        shouldFilterByKeyword,
        shouldFilterByRegion,
        shouldFilterByInterest,
        shouldFilterByEventType,
    ]);
    // create debounced callback - API call for search query update
    const debouncedSearchInputChangeHandler = useDebouncedCallback((value) => {
        // only fetch for 3 or more letter queries
        if (value.length < 3) {
            setAllParams((prevParams) => {
                const { keyword: removedKey, ...newParams } = prevParams;
                return newParams;
            });
        } else {
            setAllParams({ ...allParams, keyword: value });
        }
    });

    const resetFilters = () => {
        setAllParams([]);
        setSelections([]);
        setSearchTerm("");
    };

    const onChangeSearch = (event) => {
        event.preventDefault();
        const keyword = event?.target?.value;
        debouncedSearchInputChangeHandler(keyword);
        setSearchTerm(keyword);
    };

    const removeCurrentSelection = (filterName, label) => {
        const currentSelection = selections;
        if (filterName === "keyword") {
            setAllParams((prevParams) => {
                const { keyword: removedKey, ...newParams } = prevParams;
                return newParams;
            });
            setSearchTerm("");
        } else {
            const updatedFilterList = allParams[filterName]?.filter((filter) => filter !== label);
            setAllParams((prevParams) => ({ ...prevParams, [filterName]: updatedFilterList }));
        }
        setSelections(currentSelection?.slice(label, 1));
    };

    return (
        <section className="pb-15 lg:pb-25" id={data?.customId}>
            <div className="container">
                <div
                    className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-7.5 gap-y-5 lg:gap-y-15 pt-5 lg:pt-10.5 ${
                        selections.length ? "pb-7.5" : "pb-15"
                    }`}
                >
                    <div>
                        <input
                            type="text"
                            placeholder={searchText}
                            onChange={onChangeSearch}
                            value={searchTerm}
                            className="body w-full h-15 py-4 pl-15 pr-5 border border-stone bg-filterSearch bg-no-repeat bg-filter-search-icon rounded placeholder:text-gray"
                        />
                    </div>
                    <FilterContext.Provider value={validFilters}>
                        {eventTaxonomyTree?.map(
                            (singleTaxonomy) =>
                                singleTaxonomy.subTerms.length && (
                                    <DropdownFilterGroup
                                        key={singleTaxonomy.id}
                                        filterName={TaxonomyMapping[singleTaxonomy.name]}
                                        parentCategory={singleTaxonomy.title}
                                        categoryData={singleTaxonomy.subTerms}
                                        allParams={allParams}
                                        setAllParams={setAllParams}
                                    />
                                )
                        )}
                    </FilterContext.Provider>
                </div>
                {selections.length ? (
                    <div className="row ">
                        <div className="col-12">
                            <div className="flex flex-wrap gap-2.5 pb-15">
                                <FilterChips
                                    filters={allParams}
                                    onClose={removeCurrentSelection}
                                    onResetFilters={resetFilters}
                                    resetFiltersLabel={resetFilterText}
                                />
                            </div>
                        </div>
                    </div>
                ) : null}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-7.5 gap-y-7.5 lg:gap-y-15">
                    <div className="h-full min-h-75 p-10 bg-blue bg-eventCardBg bg-no-repeat bg-cover md:bg-contain bg-top md:bg-bottom rounded">
                        {data.cardItem?.title && (
                            <h2
                                className=" text-white"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(data.cardItem?.title) }}
                            />
                        )}
                    </div>
                    {!isLoading && !events.length && (
                        <div className="px-5 lg:px-10 col-span-2">
                            <NoResults text={noResultsText} />
                        </div>
                    )}
                    {events?.map((singleEvent) => (
                        <EventCard key={singleEvent.id} card={singleEvent} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default EventListing;
